/**
 * How an editor reacts when the value it is editing changes underneath it.
 *
 * Two surfaces on the supplier page write the same config row — the JSON editor
 * and the sender-address field — so EITHER buffer can go stale mid-edit, and
 * neither may silently discard the user's typing or silently revert the other
 * writer. Both use this one rule so they cannot disagree about what counts as a
 * conflict. Pure and separate from the components so it is unit-testable and the
 * component files keep exporting only components (Fast Refresh).
 */
export type BaseSyncDecision =
  /** Stored value now equals the buffer — nothing to do, any conflict is over. */
  | 'in-sync'
  /** Buffer was untouched since the last sync: silently take the new stored value. */
  | 'adopt'
  /** Buffer has unsaved edits AND the row moved: saving would revert someone. */
  | 'conflict';

/**
 * `buffer` is the editor's current text, `syncedValue` the stored value as of the
 * last sync, `storedValue` the stored value now.
 *
 * The `in-sync` case is what makes this non-obvious: after the user saves a DIRTY
 * buffer, the refetch hands back their own text, which differs from the old
 * baseline. Comparing only against the baseline would flag every successful save
 * as a conflict.
 */
export function baseSyncDecision(
  buffer: string,
  syncedValue: string,
  storedValue: string,
): BaseSyncDecision {
  if (buffer === storedValue) return 'in-sync';
  if (buffer === syncedValue) return 'adopt';
  return 'conflict';
}
